import React, { FC, useMemo } from 'react';

import { CellContext } from '@tanstack/react-table';

import { getUserProfileUrl } from '@adsk/acs-sc-web-platform-api';
import { EntitlementsProject, useAlloyFeatureFlag } from '@adsk/alloy-provider';
import A from '@adsk/alloy-react-a';
import { Box, Flex, Text } from '@adsk/alloy-react-box';
import Divider from '@adsk/alloy-react-divider';
import { AlertWarningIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { FEATURE_FLAGS } from '../../../constants';
import i18n from '../../../helpers/i18n';

const MFATooltipContent = () => (
  <Flex flexDirection="column">
    <Text typography="heading5">{i18n.t('MFA_REQUIRED')}</Text>
    <Text>
      {i18n.t('TWO_STEPS_VERIFICATION_IS_REQUIRED_TO_ACCESS_THIS_PROJECT')}
    </Text>
    <Divider
      style={{
        marginTop: theme.spacing.medium,
        marginBottom: theme.spacing.small,
      }}
    />
    <A
      style={{ color: theme.colors.adskBlue100 }}
      href={`${getUserProfileUrl()}/security`}
    >
      {i18n.t('SETUP_TWO_STEPS_VERIFICATION')}
    </A>
  </Flex>
);

export const NameCell: FC<
  CellContext<EntitlementsProject, EntitlementsProject['name']>
> = ({ row }) => {
  const NameWithTooltip = useMemo(
    () => (
      <Flex flexDirection="column">
        <OverflowTooltip
          triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
        >
          {row.original.name}
        </OverflowTooltip>
        {row.original.addressLine1 && (
          <OverflowTooltip
            triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
            content={row.original.addressLine1}
          >
            <Text typography="bodySmall" color="charcoal700">
              {row.original.addressLine1}
            </Text>
          </OverflowTooltip>
        )}
      </Flex>
    ),
    [row.original.name, row.original.addressLine1],
  );

  const { value: mfaSetupRequired } = useAlloyFeatureFlag(
    FEATURE_FLAGS.MfaSetupRequired,
    undefined,
  );

  return mfaSetupRequired && row.original.mfaBlocked ? (
    <Box data-testid={`ProjectName:${row.original.id}`} width="100%">
      <Flex width="100%">
        <Box>{NameWithTooltip}</Box>
        <Tooltip
          triggerTarget={OverflowTooltip.TRIGGER_TARGETS.TOOLTIP}
          content={<MFATooltipContent />}
        >
          <AlertWarningIcon
            data-testid="MFAIcon"
            style={{
              margin: `auto 0px auto ${theme.spacing.xSmall}px`,
              color: theme.colors.yellowOrange500,
            }}
          />
        </Tooltip>
      </Flex>
    </Box>
  ) : (
    NameWithTooltip
  );
};
